import React from 'react';
import Logo from '../Logo';

const NAV_LINKS = [
  { label: 'Privacy Policy', href: '/privacy' },
  { label: 'Trust & Safety', href: '/trust-and-safety' },
];

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-[#f9f9fb]">
      {/* Nav */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <a href="/">
            <Logo width="208" height="32" textFill="black" />
          </a>
          <div className="flex items-center gap-6">
            {NAV_LINKS.map(l => (
              <a key={l.href} href={l.href} className="hidden md:inline text-gray-600 text-sm font-medium hover:text-[#055EFD] transition-colors">{l.label}</a>
            ))}
            <a href="/" className="text-gray-600 text-sm font-medium hover:text-[#055EFD] transition-colors">Home</a>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-16">
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-8 md:p-12">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2 text-center font-['Montserrat']">Terms of Service</h1>
          <p className="text-center text-gray-500 text-sm mb-2">Remote Assistant Developer Platform</p>
          <div className="text-center text-gray-400 text-xs mb-12 space-y-1">
            <p><strong>Effective Date:</strong> April 1, 2026</p>
            <p><strong>Last Updated:</strong> April 1, 2026</p>
          </div>

          <div className="space-y-10 text-gray-700 text-sm leading-relaxed">

            {/* 1 */}
            <Section n="1" title="Acceptance of Terms">
              <p>
                By accessing or using the Remote Assistant Developer Platform ("Platform"), including the API at <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">api.remote-assistant.io</code>, the developer dashboard at <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">platform.remote-assistant.io</code>, and any associated SDKs, documentation, or services (collectively, the "Services"), you ("Developer," "you") agree to be bound by these Terms of Service ("Terms"). If you are accepting on behalf of a company or other legal entity, you represent that you have the authority to bind that entity.
              </p>
              <p>
                If you do not agree to these Terms, do not use the Services.
              </p>
            </Section>

            {/* 2 */}
            <Section n="2" title="Platform Description">
              <p>
                Remote Assistant provides an Infrastructure-as-a-Service (IaaS) platform that enables developers to deploy autonomous AI agents capable of simultaneous voice (PSTN/SIP), SMS, and email communication. The Platform provides:
              </p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li>RESTful APIs for assistant configuration, conversation management, and telephony</li>
                <li>Dedicated phone number provisioning (US/Canada)</li>
                <li>Real-time voice processing with speech-to-text, LLM inference, and text-to-speech</li>
                <li>SMS and email communication channels</li>
                <li>Live call monitoring, whisper guidance, and call transfer capabilities</li>
                <li>Business logic orchestration (booking, scheduling, CRM integration)</li>
                <li>Testing and simulation tools</li>
              </ul>
            </Section>

            {/* 3 */}
            <Section n="3" title="Developer Accounts & API Access">
              <p>
                To use the Platform, you must create a developer account and obtain API credentials. You are responsible for:
              </p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li>Maintaining the confidentiality of your API keys, tokens, and account credentials</li>
                <li>All activity that occurs under your account, including API calls made with your credentials</li>
                <li>Promptly notifying us of any unauthorized use of your account or credentials</li>
                <li>Ensuring that your API keys are not exposed in client-side code, public repositories, or logs</li>
              </ul>
              <p className="mt-3">
                We reserve the right to disable API credentials that we reasonably believe have been compromised.
              </p>
            </Section>

            {/* 4 */}
            <Section n="4" title="Pricing, Billing & Prepaid Balance">
              <p>
                The Platform operates on a <strong>prepaid balance model</strong>. Usage fees are deducted from your account balance in real time as API calls and telephony minutes are consumed.
              </p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li><strong>Platform Fee:</strong> A 30% platform fee is applied on top of base infrastructure costs (LLM, STT, TTS, telephony). The fee schedule is published in our pricing documentation.</li>
                <li><strong>Balance Enforcement:</strong> API calls and inbound calls may be rejected when your account balance reaches zero. You are responsible for maintaining sufficient balance for your expected usage.</li>
                <li><strong>Top-ups:</strong> Balance can be added via the developer dashboard using a valid payment method on file (processed by Stripe).</li>
                <li><strong>No Refunds:</strong> Prepaid balances are non-refundable except as required by applicable law or at our sole discretion.</li>
                <li><strong>Pricing Changes:</strong> We may modify pricing with 30 days' written notice. Continued use after the effective date constitutes acceptance.</li>
              </ul>
            </Section>

            {/* 5 */}
            <Section n="5" title="Acceptable Use Policy">
              <p>
                You agree not to use the Platform to:
              </p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li>Make unsolicited robocalls, spam calls, or automated telemarketing calls in violation of the Telephone Consumer Protection Act (TCPA), CAN-SPAM Act, or equivalent legislation in your jurisdiction</li>
                <li>Impersonate individuals, government agencies, or emergency services</li>
                <li>Engage in fraud, phishing, social engineering, or deceptive practices</li>
                <li>Harass, threaten, or abuse any person</li>
                <li>Process, store, or transmit protected health information (PHI) without a separate BAA in place</li>
                <li>Circumvent rate limits, usage restrictions, or security controls</li>
                <li>Reverse-engineer, decompile, or attempt to extract the source code of the Platform</li>
                <li>Resell or redistribute Platform access without written authorization</li>
                <li>Use the Platform in any way that violates applicable laws or regulations</li>
              </ul>
              <p className="mt-3">
                Violation of this policy may result in immediate suspension or termination of your account without notice.
              </p>
            </Section>

            {/* 6 */}
            <Section n="6" title="AI Output & Shared Responsibility">
              <p>
                The Platform utilizes large language models (LLMs) which may produce inaccurate, incomplete, or inappropriate outputs ("hallucinations"). As the deployer of AI agents built on our Platform:
              </p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li>You are the "Deployer" under the EU AI Act and bear responsibility for how AI agents interact with your end users</li>
                <li>You are responsible for configuring appropriate guardrails, system prompts, and business rules via the Studio dashboard</li>
                <li>You must monitor AI outputs and conversation logs to ensure quality and compliance</li>
                <li>You acknowledge that AI-generated responses may contain errors and should not be relied upon for legal, medical, financial, or safety-critical advice without human review</li>
              </ul>
              <p className="mt-3">
                We provide tools for monitoring, whisper guidance, and human takeover to help you maintain quality control. Use of these tools is your responsibility.
              </p>
            </Section>

            {/* 7 */}
            <Section n="7" title="Developer Responsibilities for End Users">
              <p>
                When you deploy AI agents that interact with end users (callers, SMS recipients, etc.), you are responsible for:
              </p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li>Informing end users that they are interacting with an AI system, as required by applicable law</li>
                <li>Obtaining any required consents for call recording, data collection, or automated decision-making</li>
                <li>Complying with all applicable telecommunications regulations in the jurisdictions where your agents operate</li>
                <li>Maintaining a privacy policy that accurately describes your data practices, including your use of third-party AI services</li>
                <li>Handling end-user data subject requests (access, deletion, correction) in compliance with GDPR, CCPA, and other applicable privacy laws</li>
              </ul>
            </Section>

            {/* 8 */}
            <Section n="8" title="Intellectual Property">
              <p>
                <strong>Your Content:</strong> You retain all rights to your business data, configuration, system prompts, and any content you provide to the Platform. You grant us a limited license to process this content solely to provide the Services.
              </p>
              <p>
                <strong>Our Platform:</strong> The Platform, APIs, documentation, SDKs, and all associated intellectual property remain the property of Remote Assistant. These Terms do not grant you any rights to our trademarks, service marks, or branding.
              </p>
              <p>
                <strong>Your Integrations:</strong> Applications, integrations, and products you build using our APIs are yours. We claim no ownership over your integration code or business logic.
              </p>
            </Section>

            {/* 9 */}
            <Section n="9" title="Service Availability & SLA">
              <p>
                We target 99.9% uptime for the Platform API and telephony services. However, we do not guarantee uninterrupted availability. Scheduled maintenance windows will be communicated in advance via the developer dashboard and status page.
              </p>
              <p>
                We are not liable for downtime caused by third-party infrastructure providers (AWS, Twilio, Deepgram, OpenAI), force majeure events, or your own infrastructure failures.
              </p>
              <p>
                Enterprise customers may negotiate custom SLA terms with associated service credits. Contact <a href="mailto:sales@remote-assistant.io" className="text-[#055EFD] hover:underline">sales@remote-assistant.io</a> for details.
              </p>
            </Section>

            {/* 10 */}
            <Section n="10" title="Rate Limits & Fair Use">
              <p>
                API endpoints are subject to rate limits as documented in our API reference. We reserve the right to throttle or temporarily block requests that exceed published limits or that we reasonably determine constitute abuse.
              </p>
              <p>
                If your usage patterns require higher limits, contact us to discuss enterprise tier options.
              </p>
            </Section>

            {/* 11 */}
            <Section n="11" title="Data & Privacy">
              <p>
                Your use of the Platform is subject to our <a href="/privacy" className="text-[#055EFD] hover:underline">Privacy Policy</a>. Key commitments:
              </p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li>We do not use your proprietary business data or end-user interactions to train global AI models</li>
                <li>Conversation data is processed to provide the Services and retained according to our data retention schedule</li>
                <li>You may request data export or deletion via the developer dashboard or by contacting support</li>
              </ul>
            </Section>

            {/* 12 */}
            <Section n="12" title="Indemnification">
              <p>
                You agree to indemnify, defend, and hold harmless Remote Assistant, its officers, directors, employees, and agents from any claims, damages, losses, or expenses (including reasonable attorney's fees) arising from:
              </p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li>Your use of the Platform or violation of these Terms</li>
                <li>Your AI agents' interactions with end users</li>
                <li>Any content, data, or business logic you configure on the Platform</li>
                <li>Your violation of any applicable law or regulation</li>
              </ul>
            </Section>

            {/* 13 */}
            <Section n="13" title="Limitation of Liability">
              <p>
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, REMOTE ASSISTANT'S AGGREGATE LIABILITY UNDER THESE TERMS SHALL NOT EXCEED THE AMOUNTS PAID BY YOU FOR THE SERVICES IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM.
              </p>
              <p>
                IN NO EVENT SHALL REMOTE ASSISTANT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, DATA, BUSINESS OPPORTUNITIES, OR GOODWILL, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
              </p>
              <p>
                Remote Assistant shall not be liable for any verbal or written commitments made by AI agents that exceed the parameters you configured in the Platform.
              </p>
            </Section>

            {/* 14 */}
            <Section n="14" title="Suspension & Termination">
              <p>
                <strong>By You:</strong> You may terminate your account at any time through the developer dashboard. Upon termination, your API credentials will be revoked and provisioned phone numbers will be released.
              </p>
              <p>
                <strong>By Us:</strong> We may suspend or terminate your account immediately if you violate these Terms, the Acceptable Use Policy, or if required by law. We may also suspend accounts with zero balance that have been inactive for more than 90 days.
              </p>
              <p>
                <strong>Data Retention:</strong> Upon termination, we will retain your data for 30 days to allow for export. After this period, data will be permanently deleted in accordance with our retention policy.
              </p>
            </Section>

            {/* 15 */}
            <Section n="15" title="Modifications to Terms">
              <p>
                We may update these Terms from time to time. Material changes will be communicated via email to the address associated with your developer account at least 30 days before taking effect. Continued use of the Platform after the effective date constitutes acceptance of the updated Terms.
              </p>
            </Section>

            {/* 16 */}
            <Section n="16" title="Governing Law & Dispute Resolution">
              <p>
                These Terms are governed by the laws of the Province of British Columbia, Canada, without regard to conflict of law principles. Any disputes arising under these Terms shall be resolved through binding arbitration in Vancouver, BC, except that either party may seek injunctive relief in a court of competent jurisdiction.
              </p>
            </Section>

            {/* 17 */}
            <Section n="17" title="Contact">
              <p>
                For questions about these Terms:
              </p>
              <ul className="list-none space-y-1 mt-2">
                <li><strong>Legal:</strong> <a href="mailto:legal@remote-assistant.io" className="text-[#055EFD] hover:underline">legal@remote-assistant.io</a></li>
                <li><strong>Support:</strong> <a href="mailto:support@remote-assistant.io" className="text-[#055EFD] hover:underline">support@remote-assistant.io</a></li>
                <li><strong>Enterprise Sales:</strong> <a href="mailto:sales@remote-assistant.io" className="text-[#055EFD] hover:underline">sales@remote-assistant.io</a></li>
              </ul>
            </Section>

          </div>
        </div>
      </main>

      <footer className="py-8 text-center text-gray-400 text-xs border-t border-gray-200">
        &copy; 2026 Remote Assistant. All rights reserved.
      </footer>
    </div>
  );
};

const Section = ({ n, title, children }) => (
  <div>
    <h2 className="text-lg font-bold text-gray-900 mb-3 font-['Montserrat']">
      {n}. {title}
    </h2>
    <div className="space-y-3">{children}</div>
  </div>
);

export default TermsOfService;
